import { useRef, useState, type FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { SignupShell } from './SignupShell';
import { errorKeyFor, submitSignup } from './signupApi';
import {
  SIGNUP_FIELD_ORDER,
  buildSignupPayload,
  signupLocaleFor,
  suggestSlug,
  validateSignupForm,
  type SignupFieldErrors,
  type SignupFieldName,
  type SignupFormValues,
} from './signupForm';

type PageState = 'form' | 'sent';

const EMPTY_VALUES: SignupFormValues = {
  contactName: '',
  company: '',
  email: '',
  phone: '',
  businessId: '',
  requestedSlug: '',
  consent: false,
};

/** Server rejections that belong to one field rather than the whole form. */
const SERVER_FIELD_BY_CODE: Readonly<Record<string, SignupFieldName>> = {
  invalid_business_id: 'businessId',
  company_not_found: 'businessId',
  slug_reserved: 'requestedSlug',
  slug_invalid: 'requestedSlug',
  slug_taken: 'requestedSlug',
};

const fieldId = (name: SignupFieldName): string => `signup-${name}`;

/**
 * Public self-service signup (#3017): collects the workshop's details and asks
 * the API for a verification mail. Nothing is created or charged here — the
 * payment step starts from the mail link (`SignupVerifyPage`).
 */
export default function SignupPage() {
  const { t, i18n } = useTranslation('signup');
  const [pageState, setPageState] = useState<PageState>('form');
  const [values, setValues] = useState<SignupFormValues>(EMPTY_VALUES);
  const [fieldErrors, setFieldErrors] = useState<SignupFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  // Once the person edits the address themselves, stop overwriting it from the company name.
  const slugTouched = useRef(false);

  const clearErrors = (...names: SignupFieldName[]) => {
    // An edit invalidates the message about that field, not about the others.
    setFieldErrors((current) => {
      if (names.every((name) => current[name] === undefined)) return current;
      const next = { ...current };
      for (const name of names) delete next[name];
      return next;
    });
  };

  const setField = <K extends keyof SignupFormValues>(name: K, value: SignupFormValues[K]) => {
    setValues((current) => ({ ...current, [name]: value }));
    clearErrors(name as SignupFieldName);
  };

  const handleCompanyChange = (company: string) => {
    const followSlug = !slugTouched.current;
    setValues((current) => ({
      ...current,
      company,
      requestedSlug: followSlug ? suggestSlug(company) : current.requestedSlug,
    }));
    clearErrors('company', ...(followSlug ? (['requestedSlug'] as const) : []));
  };

  const focusField = (name: SignupFieldName | undefined) => {
    if (name) document.getElementById(fieldId(name))?.focus();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inFlight.current) return;

    const errors = validateSignupForm(values);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError(t('validationSummary'));
      focusField(SIGNUP_FIELD_ORDER.find((name) => errors[name] !== undefined));
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    setFormError(null);

    const honeypot =
      (event.currentTarget.elements.namedItem('website') as HTMLInputElement | null)?.value ?? '';
    const result = await submitSignup(
      buildSignupPayload(values, signupLocaleFor(i18n.language), honeypot),
    );

    inFlight.current = false;
    setSubmitting(false);

    if (result.ok) {
      setPageState('sent');
      return;
    }

    const serverField = SERVER_FIELD_BY_CODE[result.code];
    if (serverField) {
      setFieldErrors({ [serverField]: `errors.${result.code}` });
      focusField(serverField);
      setFormError(null);
      return;
    }
    setFormError(t(errorKeyFor(result.code)));
  };

  if (pageState === 'sent') {
    return (
      <SignupShell title={t('sentTitle')}>
        <Alert severity="success" role="status" sx={{ mb: 2 }}>
          {t('sentBody', { email: values.email.trim() })}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('sentHint')}
        </Typography>
        <Button variant="outlined" onClick={() => setPageState('form')}>
          {t('sentBack')}
        </Button>
      </SignupShell>
    );
  }

  const textField = (
    name: Exclude<SignupFieldName, 'consent'>,
    extra: { hint?: string; type?: string; autoComplete?: string; required?: boolean } = {},
  ) => {
    const errorKey = fieldErrors[name];
    return (
      <TextField
        fullWidth
        required={extra.required ?? true}
        margin="normal"
        id={fieldId(name)}
        name={name}
        type={extra.type ?? 'text'}
        autoComplete={extra.autoComplete ?? 'off'}
        label={t(name)}
        value={values[name]}
        onChange={(event) => {
          const value = event.target.value;
          if (name === 'company') {
            handleCompanyChange(value);
          } else if (name === 'requestedSlug') {
            slugTouched.current = true;
            setField(name, value.toLowerCase());
          } else {
            setField(name, value);
          }
        }}
        error={errorKey !== undefined}
        helperText={errorKey !== undefined ? t(errorKey) : extra.hint}
        inputProps={{ 'aria-invalid': errorKey !== undefined }}
      />
    );
  };

  return (
    <SignupShell title={t('title')}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('intro')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('paymentNote')}
      </Typography>

      <Box component="form" onSubmit={(event) => void handleSubmit(event)} noValidate>
        {formError ? (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        ) : null}

        {textField('contactName', { autoComplete: 'name' })}
        {textField('company', { autoComplete: 'organization' })}
        {textField('email', { type: 'email', autoComplete: 'email', hint: t('emailHint') })}
        {textField('phone', { type: 'tel', autoComplete: 'tel', required: false, hint: t('phoneHint') })}
        {textField('businessId', { hint: t('businessIdHint') })}
        {textField('requestedSlug', { hint: t('requestedSlugHint') })}

        <Box
          aria-hidden="true"
          sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
        >
          <label htmlFor="signup-website">{t('website')}</label>
          <input id="signup-website" name="website" tabIndex={-1} autoComplete="off" />
        </Box>

        <FormControl error={fieldErrors.consent !== undefined} sx={{ mt: 2, display: 'block' }}>
          <FormControlLabel
            control={
              <Checkbox
                required
                id={fieldId('consent')}
                checked={values.consent}
                onChange={(event) => setField('consent', event.target.checked)}
                name="consent"
              />
            }
            label={t('consent')}
          />
          {fieldErrors.consent !== undefined ? (
            <FormHelperText>{t(fieldErrors.consent)}</FormHelperText>
          ) : null}
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={submitting}
          sx={{ mt: 3 }}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? t('submitting') : t('submit')}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        {t('haveAccount')}{' '}
        <Link component={RouterLink} to="/login" underline="hover">
          {t('goToLogin')}
        </Link>
      </Typography>
    </SignupShell>
  );
}
